import React from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { Form, Formik } from "formik";
import { toast } from "react-hot-toast";
import * as Yup from "yup";
import axios from "../../../api/axios";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import PMListbox from "../../utils/PMListbox";
import { projectParticipantsRole } from "./utils";

// Pull the human-readable validation message(s) out of an API error response.
// The API renders project errors as `{ errors: ["Project manager can't be blank", ...] }`.
const extractApiErrorMessage = (err, fallback) => {
  const apiErrors = err?.response?.data?.errors ?? err?.data?.errors;
  if (Array.isArray(apiErrors) && apiErrors.length) {
    return apiErrors.join(", ");
  }
  if (typeof apiErrors === "string" && apiErrors.trim()) {
    return apiErrors;
  }
  return fallback;
};

export const ProjectAccessDialog = ({ open, onClose, projectId, access, onSuccess }) => {
  const { t } = useTranslation();
  const isEditing = !!access;

  const handleSubmit = async (values, helpers) => {
    const fail = (message) => {
      toast.error(message);
      helpers.setSubmitting(false);
      helpers.setStatus({ success: false });
      helpers.setErrors({ submit: message });
    };

    try {
      // Get current project data
      const projectResponse = await axios.get(`/projects/${projectId}`);
      // NOTE: a global response interceptor (sessionExpiredDialog.js) RESOLVES failed
      // requests with the error response instead of rejecting, so axios never throws on
      // a 4xx/5xx. We must guard on status explicitly here.
      if (projectResponse?.status !== 200) {
        fail(extractApiErrorMessage({ response: projectResponse }, t("project.participants.save_error")));
        return;
      }
      const project = projectResponse.data;

      // Prepare participant data
      const participantData = {
        user_id: values.user_id,
        role: values.role,
        admin: values.admin || false,
        status: values.status || "ASSIGNED",
      };

      let updatedParticipants = [...(project.participants || [])];

      if (isEditing) {
        // Update existing participant
        participantData.id = access.id;
        updatedParticipants = updatedParticipants.map((p) =>
          p.id === access.id ? { ...p, ...participantData } : p
        );
      } else {
        // Add new participant
        updatedParticipants.push(participantData);
      }

      // Update project with new/updated participant
      const response = await axios.put(`/projects/${projectId}`, {
        project: {
          ...project,
          participants: updatedParticipants,
        },
      });

      if (response?.status !== 200) {
        // The project update failed validation (e.g. "Project manager må velgast").
        // Surface the real reason instead of failing silently.
        fail(extractApiErrorMessage({ response }, t("project.participants.save_error")));
        return;
      }

      // Fetch updated project to get the full participant data
      const updatedProjectResponse = await axios.get(`/projects/${projectId}`, {
        params: { fields: "participants" },
      });

      const refreshedParticipants = updatedProjectResponse.data?.participants || [];
      const savedAccess = isEditing
        ? refreshedParticipants.find((p) => p.id === access.id)
        : refreshedParticipants[refreshedParticipants.length - 1];

      if (onSuccess) {
        onSuccess(savedAccess);
      }
      toast.success(isEditing ? t("project.participants.updated") : t("project.participants.created"));
      helpers.setSubmitting(false);
    } catch (err) {
      console.error("Error saving project access:", err);
      // Surface the real reason (e.g. "Project manager må velgast") instead of a
      // generic message, so the user understands the project itself is blocking the save.
      fail(extractApiErrorMessage(err, t("project.participants.save_error")));
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t("project.participants.edit") : t("project.participants.add")}
    >
      <div className="mt-4">
        <Formik
          initialValues={{
            user_id: access?.user?.id || null,
            role: access?.role || "EXECUTOR",
            admin: access?.admin || false,
            status: access?.status || "ASSIGNED",
            submit: null,
          }}
          validationSchema={Yup.object({
            user_id: Yup.string().required(t("project.participants.user_required")),
            role: Yup.string().required(t("project.participants.role_required")),
          })}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form>
              {formik.errors.submit && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
                  <div className="flex items-start">
                    <svg
                      className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
                    </svg>
                    <p className="ml-2 text-sm text-red-700">{formik.errors.submit}</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-6">
                  <EmployeeSelector
                    label={t("project.participants.user")}
                    name="user_id"
                    selected={access?.user}
                    {...formik}
                  />
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <PMListbox
                    label={t("project.participants.role")}
                    name="role"
                    options={projectParticipantsRole}
                    selected={formik.values.role}
                    onChange={(e) => {
                      formik.setFieldValue("role", e.value);
                    }}
                    {...formik}
                  />
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <PMField
                    name="admin"
                    type="checkbox"
                    label={t("project.participants.admin_access")}
                    {...formik}
                  />
                </div>
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                  onClick={onClose}
                >
                  {t("Cancel")}
                </button>

                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:bg-gray-400"
                >
                  {formik.isSubmitting
                    ? isEditing
                      ? t("project.participants.updating")
                      : t("project.participants.adding")
                    : isEditing
                    ? t("Update")
                    : t("project.participants.add")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </PMDialog>
  );
};

